import React from "react";
import styled, { css } from "styled-components";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import {
  NEW_LOOK_PHONE_CONTROL,
  NEW_LOOK_PHONE_HIT,
  phoneMaxWidth,
} from "common/NewLook/layout.ts";
import AutoSizeText from "common/Text/AutoSizeText.tsx";

interface SegmentTabsProps<T extends string> {
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  /** Option labels; defaults to the upper-cased value ("1D"). */
  getLabel?: (value: T) => string;
  /** Underline tabs (panel headers) instead of a compact segmented track. */
  variant?: "segmented" | "underline";
}

/**
 * The dashboard's one selection control, in two forms: a compact segmented
 * track for ranges, and underline tabs for switching a panel's list.
 */
const SegmentTabs = <T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  getLabel = (option) => option.toUpperCase(),
  variant = "segmented",
}: SegmentTabsProps<T>) => (
  <Track role="tablist" aria-label={ariaLabel} $variant={variant}>
    {options.map((option) => {
      const selected = option === value;
      return (
        <Tab
          key={option}
          type="button"
          role="tab"
          aria-selected={selected}
          $selected={selected}
          $variant={variant}
          onClick={() => onChange(option)}
        >
          {variant === "segmented" ? (
            <AutoSizeText
              inheritFont={true}
              minFontSizePx={LABEL_MIN_FONT_SIZE}
            >
              {getLabel(option)}
            </AutoSizeText>
          ) : (
            getLabel(option)
          )}
        </Tab>
      );
    })}
  </Track>
);

export default SegmentTabs;

type Variant = "segmented" | "underline";
const TRACK_PAD = 2;
const LABEL_MIN_FONT_SIZE = 10;

const Track = styled.div<{ $variant: Variant }>`
  display: inline-flex;
  flex: none;
  ${(p) =>
    p.$variant === "segmented"
      ? css`
          gap: 2px;
          padding: ${TRACK_PAD}px;
          border-radius: ${NEW_LOOK_RADIUS.control}px;
          background: ${getNewLookTokens(p.theme).well};
          box-shadow: inset 0 0 0 1px ${getNewLookTokens(p.theme).hairline};
        `
      : css`
          gap: 16px;
          align-self: stretch;
        `}
`;
const Tab = styled.button<{ $selected: boolean; $variant: Variant }>`
  position: relative;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  transition: color 120ms, background-color 120ms, box-shadow 120ms;
  &:hover {
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
  ${(p) =>
    p.$variant === "segmented"
      ? css`
          height: 26px;
          min-width: 34px;
          padding: 0 8px;
          white-space: nowrap;
          border-radius: ${NEW_LOOK_RADIUS.control - 2}px;
          font-variant-numeric: tabular-nums;
          /* The label grey, not the meta grey: the track can sit on a tinted
             hero, where the lighter grey lost contrast. */
          color: ${getNewLookTokens(p.theme).secondary};
          /* Phone: a 36px track (32px tabs, at least 36 wide) whose hit
             area reaches 40px over the track's padding and gaps, so the
             track stays as wide as before. */
          @media (max-width: ${phoneMaxWidth}) {
            height: ${NEW_LOOK_PHONE_CONTROL - 2 * TRACK_PAD}px;
            min-width: ${NEW_LOOK_PHONE_HIT - 2 * TRACK_PAD}px;
            &::before {
              content: "";
              position: absolute;
              inset: ${-(NEW_LOOK_PHONE_HIT - NEW_LOOK_PHONE_CONTROL) / 2 -
                TRACK_PAD}px
                ${-TRACK_PAD}px;
            }
          }
          ${p.$selected &&
          css`
            color: ${getNewLookTokens(p.theme).ink};
            background: ${getNewLookTokens(p.theme).panel};
            box-shadow: 0 1px 2px rgba(13, 20, 78, 0.08),
              inset 0 0 0 1px ${getNewLookTokens(p.theme).hairline};
          `}
        `
      : css`
          height: 100%;
          min-height: 32px;
          padding: 0;
          &::after {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            bottom: -11px;
            height: 2px;
            border-radius: 1px;
            background: ${getNewLookTokens(p.theme).accent};
            opacity: ${p.$selected ? 1 : 0};
            transform: scaleX(${p.$selected ? 1 : 0.3});
            transition: opacity 0.2s ease, transform 0.25s ease;
          }
          ${p.$selected &&
          css`
            color: ${getNewLookTokens(p.theme).accent};
            &:hover {
              color: ${getNewLookTokens(p.theme).accent};
            }
          `}
        `}
`;
